import { NestAPI } from '@marketing-milk/frontend'
import { AdCreative, AdCreativeReview, AdCreativeReviewDTO } from '@marketing-milk/interfaces'
import { useMutation, useQueryClient } from 'react-query'
import { commentsQueryKey } from './useComments'

interface ClientApprovalInput {
  clientApproverName: string
  notes?: string
}

/**
 * Records an AE-attested client (external) approval on a creative through the
 * shared reviews controller. On success it invalidates every creative consumer
 * so the Approved badge flips in the packet, the creatives list, CreativeWizard,
 * and campaign-builder eligibility.
 */
export const useAdCreativeReview = (businessID: number, creativeID: number) => {
  const queryClient = useQueryClient()

  // Prefix-invalidate so sprint creatives, the creatives list, CreativeWizard,
  // and campaign-builder creative sources all refetch the new review status.
  // Also refresh the creative's comment thread, since approve/remove now post an
  // attested-approval event comment there.
  const invalidateCreativeConsumers = async () => {
    await Promise.all([
      queryClient.invalidateQueries(['sprints']),
      queryClient.invalidateQueries(['creatives']),
      queryClient.invalidateQueries(['creatives-by-ids']),
      queryClient.invalidateQueries(commentsQueryKey(businessID, 'creative', String(creativeID))),
    ])
  }

  const approveOnBehalfOfClient = useMutation(
    async (input: ClientApprovalInput): Promise<AdCreativeReview> => {
      const dto: AdCreativeReviewDTO = {
        approved: true,
        onBehalfOfClient: true,
        clientApproverName: input.clientApproverName,
        notes: input.notes,
      }
      const { data } = await NestAPI.post<AdCreativeReview>(
        `businesses/${businessID}/ad-creatives/${creativeID}/reviews`,
        dto
      )
      return data
    },
    { onSuccess: invalidateCreativeConsumers }
  )

  const removeApproval = useMutation(
    async (): Promise<AdCreative> => {
      const { data } = await NestAPI.delete<AdCreative>(
        `businesses/${businessID}/ad-creatives/${creativeID}/reviews`
      )
      return data
    },
    { onSuccess: invalidateCreativeConsumers }
  )

  return { approveOnBehalfOfClient, removeApproval }
}
